/**
 * Scent Showcase — component-scent-showcase.js
 * Vanilla JS, no dependencies. Handles:
 *  - Image slider (autoplay, dot navigation, fade/slide transition)
 *  - Accordion with smooth animated open/close (measures scrollHeight)
 * Autoplay pauses when the slider is off-screen (IntersectionObserver)
 * and when the tab is hidden, to save CPU/battery.
 */
(function () {
  'use strict';

  class ScentSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.scent-slider__slide'));
      this.dots = Array.from(root.querySelectorAll('[data-slide-dot]'));
      this.section = root.closest('.scent-section');

      this.autoplay = this.section.dataset.autoplay === 'true';
      this.duration = parseInt(this.section.dataset.duration, 10) || 4000;
      this.current = 0;
      this.timer = null;
      this.isVisible = true;
      this.isPaused = false;
      this.isTransitioning = false;

      if (this.slides.length <= 1) return;

      this.bindEvents();
      this.observeVisibility();

      if (this.autoplay) this.start();
    }

    bindEvents() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.slideDot, 10);
          this.goTo(index);
          this.restart();
        });
      });

      // Pause on hover for desktop mouse users only — on touch devices,
      // tapping the slide fires a synthetic mouseenter with no matching
      // mouseleave, which would otherwise pause autoplay forever after
      // a single tap.
      const supportsHover =
        window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (supportsHover) {
        this.root.addEventListener('mouseenter', () => (this.isPaused = true));
        this.root.addEventListener('mouseleave', () => (this.isPaused = false));
      }

      // Pause on keyboard focus for accessibility (works the same on all devices)
      this.root.addEventListener('focusin', () => (this.isPaused = true));
      this.root.addEventListener('focusout', () => (this.isPaused = false));

      document.addEventListener('visibilitychange', () => {
        this.isPaused = document.hidden;
      });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(this.root);
    }

    goTo(index) {
      // Guard against overlapping transitions (e.g. rapid double dot-clicks)
      // so the slider never has two transitions racing at once.
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      setTimeout(() => {
        this.isTransitioning = false;
      }, 1400); // matches the opacity/visibility transition duration in the CSS

      const total = this.slides.length;
      const nextIndex = (index + total) % total;

      this.slides[this.current].classList.remove('is-active');
      this.slides[nextIndex].classList.add('is-active');

      if (this.dots[this.current]) {
        this.dots[this.current].classList.remove('is-active');
        this.dots[this.current].setAttribute('aria-selected', 'false');
      }
      if (this.dots[nextIndex]) {
        this.dots[nextIndex].classList.add('is-active');
        this.dots[nextIndex].setAttribute('aria-selected', 'true');
      }

      this.current = nextIndex;
    }

    next() {
      this.goTo(this.current + 1);
    }

    start() {
      if (this.timer) return;
      this.timer = setInterval(() => {
        if (this.isVisible && !this.isPaused) this.next();
      }, this.duration);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      if (this.autoplay) this.start();
    }
  }

  class ScentAccordion {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.scent-section');
      this.multiOpen = this.section.dataset.multiOpen === 'true';
      this.items = Array.from(root.querySelectorAll('.scent-accordion__item'));

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const panel = item.querySelector('[data-accordion-panel]');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', () => this.toggle(trigger, panel));
      });

      // Re-measure open panels on resize (e.g. orientation change, font load)
      window.addEventListener('resize', this.debounce(() => this.refreshOpen(), 150));
    }

    toggle(trigger, panel) {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (!this.multiOpen) {
        this.items.forEach((item) => {
          const otherTrigger = item.querySelector('[data-accordion-trigger]');
          const otherPanel = item.querySelector('[data-accordion-panel]');
          if (otherTrigger !== trigger && otherTrigger.getAttribute('aria-expanded') === 'true') {
            this.close(otherTrigger, otherPanel);
          }
        });
      }

      if (isOpen) {
        this.close(trigger, panel);
      } else {
        this.open(trigger, panel);
      }
    }

    open(trigger, panel) {
      trigger.setAttribute('aria-expanded', 'true');
      const height = panel.scrollHeight;
      panel.style.maxHeight = height + 'px';

      // After the transition ends, allow natural resizing (e.g. content reflow)
      const onEnd = (e) => {
        if (e.propertyName !== 'max-height') return;
        if (trigger.getAttribute('aria-expanded') === 'true') {
          panel.style.maxHeight = 'none';
        }
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
    }

    close(trigger, panel) {
      // If currently 'none', set explicit height first so the transition can animate from it
      if (panel.style.maxHeight === 'none' || panel.style.maxHeight === '') {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }
      // Force reflow so the browser registers the starting height before collapsing
      // eslint-disable-next-line no-unused-expressions
      panel.offsetHeight;
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.maxHeight = '0px';
    }

    refreshOpen() {
      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const panel = item.querySelector('[data-accordion-panel]');
        if (trigger && trigger.getAttribute('aria-expanded') === 'true') {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-scent-slider]').forEach((el) => {
      if (!el.dataset.initialized) {
        new ScentSlider(el);
        el.dataset.initialized = 'true';
      }
    });
    scope.querySelectorAll('[data-scent-accordion]').forEach((el) => {
      if (!el.dataset.initialized) {
        new ScentAccordion(el);
        el.dataset.initialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-init when section is added/re-rendered in the theme editor
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
